import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { teamsApi } from '@/utils/api';
import { useTaskDrawer } from '@/contexts/TaskDrawerContext';
import { useCaptureModal } from '@/contexts/CaptureModalContext';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, FileText, Mic, ExternalLink, Plus, CheckSquare, Users } from 'lucide-react';
import { taskRYG, rygFromTasks, aggregateRYG, RYG_META, RYG_ORDER, todayStr } from '@/utils/ryg';
import { relTime } from '@/utils/time';

const STPILL = (s) => s === 'done' ? { c: 'cp', l: 'Completed' } : s === 'in_progress' ? { c: 'ip', l: 'In progress' } : { c: 'ns', l: 'Not started' };

export default function WorkspaceScopedView() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { openTaskDrawer } = useTaskDrawer();
  const { openModal } = useCaptureModal();
  const [team, setTeam] = useState(null);
  const [cards, setCards] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [rygFilter, setRygFilter] = useState(null);

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const r = await teamsApi.overview(teamId);
      setTeam(r.data.team || null);
      setCards(r.data.cards || []);
      setTasks(r.data.tasks || []);
    } catch (e) {
      if (e?.response?.status === 404) setTeam(null);
      else { setLoadError(true); toast.error('Could not load workspace'); }
    } finally { setLoading(false); }
  }, [teamId]);
  useEffect(() => { load(); }, [load]);

  const _today = todayStr();
  const ryg = rygFromTasks(tasks, _today);
  const visibleTasks = (rygFilter ? tasks.filter(t => taskRYG(t, _today) === rygFilter) : tasks)
    .filter(t => !t.parent_task_id)
    .sort((a, b) => (a.due_date || '9999').localeCompare(b.due_date || '9999'));

  if (loading) {
    return <div className="pagewrap"><Skeleton className="h-10 w-64 mb-4" />{[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full mb-2 rounded-xl" />)}</div>;
  }
  if (!team) {
    return (
      <div className="pagewrap">
        <button className="back" onClick={() => navigate('/')} data-testid="wsv-back"><ArrowLeft size={15} />Home</button>
        {loadError ? (
          <div className="ac-empty" data-testid="wsv-load-error"><div className="et">Couldn't load this workspace</div>
            <div className="es">Something went wrong on our side — the workspace still exists.</div>
            <button className="btn-green mt-3" data-testid="wsv-retry" onClick={() => { setLoading(true); load(); }}>Try again</button></div>
        ) : (
          <div className="ac-empty" data-testid="wsv-notfound"><div className="et">Workspace not found</div>
            <div className="es">It may have been deleted or you no longer have access.</div></div>
        )}
      </div>
    );
  }

  const displayName = team.name;

  return (
    <div className="pagewrap home-v2" data-testid="workspace-scoped-view">
      <button className="back" onClick={() => navigate('/')} data-testid="wsv-back"><ArrowLeft size={15} />Home</button>

      <div className="page-head">
        <div>
          <h1 data-testid="wsv-name">{displayName}</h1>
          <p className="sub">{(team.members || []).length} member{(team.members || []).length === 1 ? '' : 's'} · {cards.length} card{cards.length === 1 ? '' : 's'} · {tasks.filter(t => t.status !== 'done').length} open</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost" data-testid="wsv-open-hub"
            onClick={() => navigate('/workspace', { state: { openTeam: teamId } })}>
            <ExternalLink size={15} />Full workspace
          </button>
          {team.is_member && team.my_role !== 'viewer' && (
            <button className="btn-capture-top" onClick={openModal} data-testid="wsv-capture"><Plus size={16} />Capture</button>
          )}
          {team.my_role === 'viewer' && (
            <span className="text-[11px] font-semibold uppercase px-2 py-1 rounded-full" style={{ background: 'rgba(154,163,176,.15)', color: '#7c8694' }} data-testid="wsv-viewer-badge">Viewer</span>
          )}
        </div>
      </div>

      {/* RYG rollup / filter */}
      <div className="ryg-bar" data-testid="wsv-ryg-bar">
        <span className="ryg-lbl">Status</span>
        {RYG_ORDER.map(k => (
          <button key={k} className={`ryg-chip${rygFilter === k ? ' on' : ''}`} data-testid={`wsv-ryg-${k}`}
            onClick={() => setRygFilter(rygFilter === k ? null : k)}
            style={rygFilter === k ? { borderColor: RYG_META[k].color, background: RYG_META[k].bg } : undefined}>
            <span className="ryg-dot" style={{ background: RYG_META[k].color }} />{RYG_META[k].label}<b>{ryg[k]}</b>
          </button>
        ))}
      </div>

      <div className="layout">
        {/* Action Cards */}
        <div className="col-main">
          <div className="sec-row"><div className="sh"><h3>Action Cards</h3><span className="n">({cards.length})</span></div></div>
          <div className="list-card" data-testid="wsv-cards">
            {cards.length === 0 ? (
              <div className="home-empty"><div className="eic"><FileText size={20} /></div><div className="et">No Action Cards here yet</div></div>
            ) : cards.map(c => (
              <div key={c.id} className="row" role="button" tabIndex={0} data-testid={`wsv-card-${c.id}`}
                onClick={() => navigate(`/action-cards/${c.id}`)}>
                <div className="r-name">
                  <div className={`type-ic ${c.source === 'audio' ? 'green' : ''}`}>{c.source === 'audio' ? <Mic size={13} /> : <FileText size={13} />}</div>
                  <span className="tt">{c.title || c.contact_name || 'Untitled card'}</span>
                </div>
                <div className="r-tk"><b>{c.task_ids?.length || 0}</b></div>
                <div className="r-when">{relTime(c.updated_at || c.created_at)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tasks */}
        <aside className="col-rail">
          <div className="rail-card" data-testid="wsv-tasks">
            <div className="rail-h"><span className="lab">Tasks</span><span className="right" style={{ marginLeft: 8 }}>{visibleTasks.length} shown</span></div>
            {visibleTasks.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--mute)', fontStyle: 'italic', padding: '10px 16px' }}>No tasks{rygFilter ? ' in this status' : ' yet'}.</p>
            ) : visibleTasks.map(t => {
              const st = STPILL(t.status);
              return (
                <div key={t.id} className="st-row" role="button" tabIndex={0} data-testid={`wsv-task-${t.id}`}
                  onClick={() => openTaskDrawer(t.id, { onChanged: load })} style={{ cursor: 'pointer' }}>
                  <div className="top">
                    <span className="k" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="ryg-dot" title={RYG_META[taskRYG(t, _today)].label} style={{ background: RYG_META[taskRYG(t, _today)].color }} />
                      {t.title}
                    </span>
                    <span className={`stpill ${st.c}`}>{st.l}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </aside>
      </div>
    </div>
  );
}
